// Slippage and decimal-scale helpers for PositionRouter queue hooks.
//
// The router operates across three decimal scales:
//   - Wrapper ERC-20 shares (18-dec): wsTAO and wAlpha base units.
//   - Native TAO (18-dec wei): what wsTAO.unwrapTo delivers.
//   - Native alpha (9-dec RAO): what wAlpha.unwrapTo delivers.
// `minOut` on bundled-flow legs is interpreted in different scales by the
// dispatcher (see PositionRouter.sol BorrowLeg / RedeemLeg comments). The
// slippage tolerance must always apply to the asset the user actually
// receives, so unwrap floors are computed in native units.

export const RAO_WEI = 10n ** 9n;
export const ONE_TOKEN = 10n ** 18n;
export const TEN_MINUTES_SECONDS = 10n * 60n;

// Truncates the sub-RAO remainder. WsTAO.wrap requires msg.value to be a
// clean multiple of WEI_TO_RAO (1e9).
export const floorToRao = (amountWei: bigint): bigint => (amountWei / RAO_WEI) * RAO_WEI;

export const calculateSlippageFloor = ({
  amount,
  slippagePercentage,
}: {
  amount: bigint;
  slippagePercentage: number;
}): bigint => {
  if (!Number.isFinite(slippagePercentage) || slippagePercentage < 0 || slippagePercentage > 100) {
    throw new Error('calculateSlippageFloor: slippagePercentage must be between 0 and 100');
  }

  const slippageBpsFloat = slippagePercentage * 100;
  const slippageBpsNumber = Math.round(slippageBpsFloat);
  if (Math.abs(slippageBpsFloat - slippageBpsNumber) > 1e-9) {
    throw new Error(
      'calculateSlippageFloor: slippagePercentage must resolve to integer basis points',
    );
  }

  const slippageBps = BigInt(slippageBpsNumber);
  return (amount * (10000n - slippageBps)) / 10000n;
};

// Native → wrapper floor: sharesOut = (nativeAmount * 1e18) / exchangeRate,
// where exchangeRate is "native base units per 1e18 wrapper shares"
// (wsTAO/wAlpha convention; see WsTAO.sol:22-23 NatSpec).
export const computeWrappedOutFloor = ({
  nativeAmount,
  exchangeRate,
  slippagePercentage,
}: {
  nativeAmount: bigint;
  exchangeRate: bigint;
  slippagePercentage: number;
}): bigint => {
  if (exchangeRate <= 0n) {
    throw new Error('computeWrappedOutFloor: exchangeRate must be positive');
  }
  const wrappedAmountOut = (nativeAmount * ONE_TOKEN) / exchangeRate;
  return calculateSlippageFloor({ amount: wrappedAmountOut, slippagePercentage });
};

// Wrapper → native floor: nativeOut = (wrapperAmount * exchangeRate) / 1e18,
// matching WsTAO._unwrapTo:356 and the corresponding WAlpha path. The floor
// MUST be in native units — computing it in wrapper units misscales by the
// rate ratio (silently weakens or revert-storms depending on direction).
export const computeNativeOutFloor = ({
  wrapperAmount,
  exchangeRate,
  slippagePercentage,
}: {
  wrapperAmount: bigint;
  exchangeRate: bigint;
  slippagePercentage: number;
}): bigint => {
  if (exchangeRate <= 0n) {
    throw new Error('computeNativeOutFloor: exchangeRate must be positive');
  }
  const nativeAmountOut = (wrapperAmount * exchangeRate) / ONE_TOKEN;
  return calculateSlippageFloor({ amount: nativeAmountOut, slippagePercentage });
};

// 10-minute window covers wallet-prompt + confirmation latency on Bittensor testnet.
export const computeRouterDeadline = (): bigint =>
  BigInt(Math.floor(Date.now() / 1000)) + TEN_MINUTES_SECONDS;
